import { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer, Views, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  Appointment,
  SERVICE_TYPES,
  STATUS_OPTIONS,
} from '@/lib/appointments-service';
import AppointmentDetailDialog from '@/components/admin/appointments/AppointmentDetailDialog';
import CreateAppointmentDialog from '@/components/admin/appointments/CreateAppointmentDialog';
import SEO from '@/components/SEO';
import { 
  Search, 
  Plus,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  Calendar as CalendarIcon,
  RefreshCw,
  List,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Setup the localizer for react-big-calendar
const locales = {
  'en-US': enUS,
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Calendar event interface
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: Appointment | GoogleCalendarEvent;
  source: 'booking' | 'google_calendar';
}

// Status colors for events
const statusColors: Record<string, { bg: string; border: string; text: string }> = {
  pending: { bg: 'hsl(var(--warning) / 0.2)', border: 'hsl(var(--warning))', text: 'hsl(var(--warning-foreground))' },
  paid: { bg: 'hsl(var(--success) / 0.2)', border: 'hsl(var(--success))', text: 'hsl(var(--success-foreground))' },
  confirmed: { bg: 'hsl(var(--primary) / 0.2)', border: 'hsl(var(--primary))', text: 'hsl(var(--primary-foreground))' },
  completed: { bg: 'hsl(var(--muted) / 0.5)', border: 'hsl(var(--muted-foreground))', text: 'hsl(var(--muted-foreground))' },
  cancelled: { bg: 'hsl(var(--destructive) / 0.15)', border: 'hsl(var(--destructive))', text: 'hsl(var(--destructive))' },
  failed: { bg: 'hsl(var(--destructive) / 0.15)', border: 'hsl(var(--destructive))', text: 'hsl(var(--destructive))' },
  test: { bg: 'hsl(270 50% 60% / 0.2)', border: 'hsl(270 50% 50%)', text: 'hsl(270 50% 30%)' },
  google_calendar: { bg: 'hsl(210 100% 50% / 0.15)', border: 'hsl(210 100% 45%)', text: 'hsl(210 100% 35%)' },
};

// Google Calendar event interface
interface GoogleCalendarEvent {
  id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string;
  status: string;
  htmlLink: string;
  creator: string;
  attendees: Array<{ email: string; displayName: string; responseStatus: string }>;
  source: 'google_calendar';
}

// Helper to check if resource is a booking
const isBooking = (resource: Appointment | GoogleCalendarEvent): resource is Appointment => {
  return 'booking_id' in resource;
};

// Custom event component
const EventComponent = ({ event }: { event: CalendarEvent }) => {
  const isGoogleEvent = event.source === 'google_calendar';
  const status = isGoogleEvent 
    ? 'google_calendar' 
    : (event.resource as Appointment).payment_status || 'pending';
  const colors = statusColors[status] || statusColors.pending;
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="px-1.5 py-0.5 rounded text-xs truncate h-full cursor-pointer transition-opacity hover:opacity-80"
          style={{
            backgroundColor: colors.bg,
            borderLeft: `3px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <span className="font-medium truncate">
            {isGoogleEvent && '📅 '}
            {event.title}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="space-y-1.5">
          {isGoogleEvent ? (
            <>
              <p className="font-semibold">{event.title}</p>
              <p className="text-xs text-muted-foreground">
                {(event.resource as GoogleCalendarEvent).description || 'No description'}
              </p>
              {(event.resource as GoogleCalendarEvent).location && (
                <p className="text-xs text-muted-foreground">
                  📍 {(event.resource as GoogleCalendarEvent).location}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                  Google Calendar
                </Badge>
              </div>
            </>
          ) : (
            <>
              <p className="font-semibold">{(event.resource as Appointment).full_name}</p>
              <p className="text-xs text-muted-foreground">{(event.resource as Appointment).email}</p>
              <p className="text-xs text-muted-foreground">{(event.resource as Appointment).phone}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{(event.resource as Appointment).service}</Badge>
                <Badge 
                  variant="outline" 
                  className="text-xs capitalize"
                  style={{ borderColor: colors.border, color: colors.text }}
                >
                  {status}
                </Badge>
              </div>
            </>
          )}
          <p className="text-xs">
            {format(event.start, 'PPp')}
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const AdminCalendar = () => {
  const { toast } = useToast();
  const { role } = useAuth();
  
  // Check if user can edit (superadmin or admin)
  const canEdit = role === 'superadmin' || role === 'admin';
  
  // State
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.MONTH);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialogs
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedGoogleEvent, setSelectedGoogleEvent] = useState<GoogleCalendarEvent | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showGoogleEventDialog, setShowGoogleEventDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>(undefined);

  // Fetch Google Calendar events
  const fetchGoogleCalendarEvents = useCallback(async () => {
    try {
      setLoadingGoogle(true);
      
      const rangeStart = startOfMonth(subMonths(currentDate, 1));
      const rangeEnd = endOfMonth(addMonths(currentDate, 1));
      
      const { data, error } = await supabase.functions.invoke('get-google-calendar-events', {
        body: {
          startDate: rangeStart.toISOString(),
          endDate: rangeEnd.toISOString(),
          timezone: 'America/Chicago',
        },
      });

      if (error) {
        console.error('Error fetching Google Calendar events:', error);
        return;
      }

      if (data?.events) {
        setGoogleEvents(data.events);
        console.log(`Loaded ${data.events.length} Google Calendar events`);
      }
    } catch (error) {
      console.error('Error fetching Google Calendar:', error);
    } finally {
      setLoadingGoogle(false);
    }
  }, [currentDate]);

  // Fetch appointments for the current view range
  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on current view (fetch 2 months to cover navigation)
      const rangeStart = startOfMonth(subMonths(currentDate, 1));
      const rangeEnd = endOfMonth(addMonths(currentDate, 1));
      
      let query = supabase
        .from('bookings')
        .select('*')
        .not('appointment_start', 'is', null)
        .gte('appointment_start', rangeStart.toISOString())
        .lte('appointment_start', rangeEnd.toISOString())
        .order('appointment_start', { ascending: true });
      
      // Apply status filter
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('payment_status', statusFilter);
      }
      
      // Apply service filter
      if (serviceFilter && serviceFilter !== 'all') {
        query = query.eq('service', serviceFilter);
      }
      
      // Apply search filter
      if (searchTerm) {
        query = query.or(
          `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
        );
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setAppointments((data || []) as Appointment[]);
    } catch (error: any) {
      console.error('Error fetching appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointments',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentDate, statusFilter, serviceFilter, searchTerm, toast]);

  useEffect(() => {
    fetchAppointments();
    fetchGoogleCalendarEvents();
  }, [fetchAppointments, fetchGoogleCalendarEvents]);

  // Convert appointments and Google events to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    // Booking events
    const bookingEvents: CalendarEvent[] = appointments
      .filter(apt => apt.appointment_start)
      .map(apt => {
        const start = new Date(apt.appointment_start!);
        const end = apt.appointment_end 
          ? new Date(apt.appointment_end) 
          : new Date(start.getTime() + 60 * 60 * 1000);
        
        const clientName = apt.full_name.split(' ')[0];
        const title = `${clientName} - ${apt.service}`;
        
        return {
          id: apt.id,
          title,
          start,
          end,
          resource: apt,
          source: 'booking' as const,
        };
      });

    // Google Calendar events
    const googleCalendarEvents: CalendarEvent[] = showGoogleEvents 
      ? googleEvents.map(evt => ({
          id: evt.id,
          title: evt.title,
          start: new Date(evt.start),
          end: new Date(evt.end),
          resource: evt,
          source: 'google_calendar' as const,
        }))
      : [];

    return [...bookingEvents, ...googleCalendarEvents];
  }, [appointments, googleEvents, showGoogleEvents]);

  // Handle event click
  const handleSelectEvent = (event: CalendarEvent) => {
    if (event.source === 'google_calendar') {
      setSelectedGoogleEvent(event.resource as GoogleCalendarEvent);
      setShowGoogleEventDialog(true);
    } else {
      setSelectedAppointment(event.resource as Appointment);
      setShowDetailDialog(true);
    }
  };

  // Handle slot selection (for creating new appointments)
  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    if (canEdit) {
      setDefaultDate(slotInfo.start);
      setShowCreateDialog(true);
    }
  };

  // Navigation handlers
  const handleNavigate = (action: 'PREV' | 'NEXT' | 'TODAY') => {
    const newDate = new Date(currentDate);
    
    switch (action) {
      case 'PREV':
        if (currentView === Views.MONTH) {
          newDate.setMonth(newDate.getMonth() - 1);
        } else if (currentView === Views.WEEK) {
          newDate.setDate(newDate.getDate() - 7);
        } else {
          newDate.setDate(newDate.getDate() - 1);
        }
        break;
      case 'NEXT':
        if (currentView === Views.MONTH) {
          newDate.setMonth(newDate.getMonth() + 1);
        } else if (currentView === Views.WEEK) {
          newDate.setDate(newDate.getDate() + 7);
        } else {
          newDate.setDate(newDate.getDate() + 1);
        }
        break;
      case 'TODAY':
        setCurrentDate(new Date());
        return;
    }
    
    setCurrentDate(newDate);
  };

  // Custom toolbar label
  const getToolbarLabel = () => {
    switch (currentView) {
      case Views.MONTH:
        return format(currentDate, 'MMMM yyyy');
      case Views.WEEK:
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case Views.DAY:
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setServiceFilter('all');
    setStatusFilter('all');
  };

  // Event style getter
  const eventStyleGetter = (event: CalendarEvent) => {
    const status = event.source === 'google_calendar' 
      ? 'google_calendar' 
      : (event.resource as Appointment).payment_status || 'pending';
    const colors = statusColors[status] || statusColors.pending;
    
    return {
      style: {
        backgroundColor: colors.bg,
        borderLeft: `3px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '4px',
        fontSize: '12px',
        padding: '2px 4px',
      },
    };
  };

  return (
    <>
      <SEO 
        title="Calendar - Admin"
        description="View appointments in calendar format"
      />
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Appointments Calendar</h1>
            <p className="text-muted-foreground">
              Visual overview of all scheduled appointments
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/appointments">
                <List className="h-4 w-4 mr-2" />
                List View
              </Link>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { fetchAppointments(); fetchGoogleCalendarEvents(); }}
              disabled={loading || loadingGoogle}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${(loading || loadingGoogle) ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => { setDefaultDate(undefined); setShowCreateDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                New Appointment
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {SERVICE_TYPES.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>

            {/* Status Legend */}
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Status:</span>
              {STATUS_OPTIONS.map((status) => {
                const colors = statusColors[status.value] || statusColors.pending;
                return (
                  <div key={status.value} className="flex items-center gap-1.5">
                    <div 
                      className="w-3 h-3 rounded-sm"
                      style={{ 
                        backgroundColor: colors.bg,
                        borderLeft: `3px solid ${colors.border}`,
                      }}
                    />
                    <span className="text-xs text-muted-foreground">{status.label}</span>
                  </div>
                );
              })}
              
              {/* Google Calendar toggle */}
              <div className="ml-auto flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div 
                    className="w-3 h-3 rounded-sm"
                    style={{ 
                      backgroundColor: statusColors.google_calendar.bg,
                      borderLeft: `3px solid ${statusColors.google_calendar.border}`,
                    }}
                  />
                  <span className="text-xs text-muted-foreground">Google Calendar</span>
                </div>
                <Button
                  variant={showGoogleEvents ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setShowGoogleEvents(!showGoogleEvents)}
                  className="h-7"
                >
                  {showGoogleEvents ? 'Hide' : 'Show'} Google Events
                  {loadingGoogle && <RefreshCw className="ml-1 h-3 w-3 animate-spin" />}
                  {!loadingGoogle && googleEvents.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                      {googleEvents.length}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardHeader className="pb-4">
            {/* Custom Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {/* Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleNavigate('TODAY')}>
                  Today
                </Button>
                <div className="flex items-center">
                  <Button variant="ghost" size="icon" onClick={() => handleNavigate('PREV')}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleNavigate('NEXT')}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <h2 className="text-lg font-semibold min-w-[200px]">{getToolbarLabel()}</h2>
              </div>

              {/* View Toggles */}
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={currentView === Views.MONTH ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView(Views.MONTH)}
                >
                  <CalendarDays className="h-4 w-4 mr-1.5" />
                  Month
                </Button>
                <Button
                  variant={currentView === Views.WEEK ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView(Views.WEEK)}
                >
                  <CalendarRange className="h-4 w-4 mr-1.5" />
                  Week
                </Button>
                <Button
                  variant={currentView === Views.DAY ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentView(Views.DAY)}
                >
                  <CalendarIcon className="h-4 w-4 mr-1.5" />
                  Day
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-muted-foreground">Loading appointments...</p>
                </div>
              </div>
            ) : events.length === 0 ? (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center">
                  <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No appointments in this period</p>
                  <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or date range</p>
                </div>
              </div>
            ) : (
              <div className="calendar-wrapper">
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 600 }}
                  view={currentView}
                  date={currentDate}
                  onNavigate={(date) => setCurrentDate(date)}
                  onView={(view) => setCurrentView(view)}
                  onSelectEvent={handleSelectEvent}
                  onSelectSlot={handleSelectSlot}
                  selectable={canEdit}
                  popup
                  components={{
                    event: EventComponent,
                  }}
                  eventPropGetter={eventStyleGetter}
                  toolbar={false}
                  formats={{
                    dayFormat: 'EEE d',
                    weekdayFormat: 'EEE',
                    monthHeaderFormat: 'MMMM yyyy',
                    dayHeaderFormat: 'EEEE, MMMM d',
                    dayRangeHeaderFormat: ({ start, end }) =>
                      `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`,
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <AppointmentDetailDialog
        appointment={selectedAppointment}
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        onAppointmentUpdated={fetchAppointments}
        canEdit={canEdit}
      />

      <CreateAppointmentDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onAppointmentCreated={() => { fetchAppointments(); fetchGoogleCalendarEvents(); }}
        defaultDate={defaultDate}
      />

      {/* Google Calendar Event Dialog */}
      {selectedGoogleEvent && (
        <Dialog open={showGoogleEventDialog} onOpenChange={setShowGoogleEventDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-600" />
                {selectedGoogleEvent.title}
              </DialogTitle>
              <DialogDescription>
                Google Calendar Event
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2 text-sm">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <span>
                  {format(new Date(selectedGoogleEvent.start), 'PPp')} - {format(new Date(selectedGoogleEvent.end), 'p')}
                </span>
              </div>
              
              {selectedGoogleEvent.location && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="text-muted-foreground">📍</span>
                  <span>{selectedGoogleEvent.location}</span>
                </div>
              )}
              
              {selectedGoogleEvent.description && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Description</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedGoogleEvent.description}
                  </p>
                </div>
              )}
              
              {selectedGoogleEvent.attendees && selectedGoogleEvent.attendees.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Attendees</p>
                  <div className="space-y-1">
                    {selectedGoogleEvent.attendees.map((attendee, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>• {attendee.displayName || attendee.email}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {attendee.responseStatus}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedGoogleEvent.htmlLink && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => window.open(selectedGoogleEvent.htmlLink, '_blank')}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Open in Google Calendar
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Custom Calendar Styles */}
      <style>{`
        .calendar-wrapper .rbc-calendar {
          font-family: inherit;
        }
        
        .calendar-wrapper .rbc-header {
          padding: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          color: hsl(var(--muted-foreground));
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .calendar-wrapper .rbc-month-view {
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          overflow: hidden;
        }
        
        .calendar-wrapper .rbc-month-row {
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .calendar-wrapper .rbc-day-bg {
          transition: background-color 0.15s ease;
        }
        
        .calendar-wrapper .rbc-day-bg:hover {
          background-color: hsl(var(--muted) / 0.5);
        }
        
        .calendar-wrapper .rbc-off-range-bg {
          background-color: hsl(var(--muted) / 0.3);
        }
        
        .calendar-wrapper .rbc-today {
          background-color: hsl(var(--primary) / 0.08);
        }
        
        .calendar-wrapper .rbc-date-cell {
          padding: 4px 8px;
          text-align: right;
          font-size: 0.875rem;
        }
        
        .calendar-wrapper .rbc-date-cell.rbc-now {
          font-weight: 600;
          color: hsl(var(--primary));
        }
        
        .calendar-wrapper .rbc-event {
          border: none;
          padding: 0;
          background: transparent;
        }
        
        .calendar-wrapper .rbc-event:focus {
          outline: 2px solid hsl(var(--ring));
          outline-offset: 2px;
        }
        
        .calendar-wrapper .rbc-event-content {
          overflow: hidden;
        }
        
        .calendar-wrapper .rbc-row-segment {
          padding: 1px 2px;
        }
        
        .calendar-wrapper .rbc-show-more {
          color: hsl(var(--primary));
          font-size: 0.75rem;
          font-weight: 500;
          background: transparent;
          padding: 2px 4px;
        }
        
        .calendar-wrapper .rbc-time-view {
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          overflow: hidden;
        }
        
        .calendar-wrapper .rbc-time-header {
          border-bottom: 1px solid hsl(var(--border));
        }
        
        .calendar-wrapper .rbc-time-content {
          border-top: none;
        }
        
        .calendar-wrapper .rbc-timeslot-group {
          border-bottom: 1px solid hsl(var(--border) / 0.5);
        }
        
        .calendar-wrapper .rbc-time-slot {
          font-size: 0.75rem;
          color: hsl(var(--muted-foreground));
        }
        
        .calendar-wrapper .rbc-day-slot .rbc-time-slot {
          border-top: 1px solid hsl(var(--border) / 0.3);
        }
        
        .calendar-wrapper .rbc-current-time-indicator {
          background-color: hsl(var(--destructive));
          height: 2px;
        }
        
        .calendar-wrapper .rbc-current-time-indicator::before {
          content: '';
          position: absolute;
          left: -5px;
          top: -4px;
          width: 10px;
          height: 10px;
          background-color: hsl(var(--destructive));
          border-radius: 50%;
        }
        
        .calendar-wrapper .rbc-overlay {
          background: hsl(var(--popover));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          padding: 8px;
        }
        
        .calendar-wrapper .rbc-overlay-header {
          font-weight: 600;
          padding: 4px 8px;
          border-bottom: 1px solid hsl(var(--border));
          margin-bottom: 4px;
        }
        
        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .calendar-wrapper .rbc-toolbar {
            flex-direction: column;
            gap: 8px;
          }
          
          .calendar-wrapper .rbc-month-view .rbc-header {
            padding: 4px;
            font-size: 0.75rem;
          }
          
          .calendar-wrapper .rbc-date-cell {
            padding: 2px 4px;
            font-size: 0.75rem;
          }
          
          .calendar-wrapper .rbc-row-segment {
            padding: 1px;
          }
        }
      `}</style>
    </>
  );
};

export default AdminCalendar;
